import { getChartExportWarning } from "../../common/export/exportWarnings.ts";
import ChartReportExport from "../../common/export/ChartReportExport.tsx";
import type { ExportChartFormats } from "../../viewer/chart/ChartProps.ts";
import { useRef } from "react";
import { useSelector } from "react-redux";
import useChartStateViewer from "../../viewer/chart/hooks/useChartStateViewer.ts";
import { useRequestConfigViewer } from "../../viewer/chart/hooks/useRequestConfigViewer.ts";
import PreviewComponent from "../../viewer/chart/PreviewComponent.tsx";
import PreviewActions from "../../viewer/common/PreviewActions.tsx";
import ViewContainer from "../../viewer/common/ViewContainer.tsx";
import ViewerParameters from "../../viewer/common/ViewerParameters.tsx";
import type { ChartRunState } from "../../viewer/storage/runState.ts";
import { useBiCommonDataContext } from "../../../contexts/BiCommonDataContext.ts";
import biApi from "../../../../../../../api/biApi.ts";
import {
  selectCurrentReportName,
  selectCurrentReportType,
  selectPreviewMode,
  selectSessionId,
} from "../../../store/selectors/currentReportSelectors";
import { selectLogQueries } from "../../../store/selectors/devToolsSelectors";
import ChangePreviewModeButton from "../common/ChangePreviewModeButton.tsx";
import useCommonReportHandlers from "../common/hooks/useCommonReportHandlers.ts";

interface Props {
  runState: ChartRunState;
}
export default function ChartViewer({ runState }: Props) {
  const exportChartRef = useRef<((fileName: string, format: ExportChartFormats) => void) | undefined>(undefined);
  const reportName = useSelector(selectCurrentReportName);
  const reportType = useSelector(selectCurrentReportType);
  const isQueryLoggingActive = useSelector(selectLogQueries);
  const handlers = useCommonReportHandlers(runState.values);
  const { conditions, onRefreshGrid, refreshToken } = useBiCommonDataContext();
  const previewMode = useSelector(selectPreviewMode);
  const sessionId = useSelector(selectSessionId);

  const { requestConfig: viewConfig } = useRequestConfigViewer({
    values: runState.values,
    isQueryLoggingActive,
    sessionId,
  });

  const state = useChartStateViewer({
    dimensions: runState.dimensions,
    conditions,
    argumentFields: runState.arguments,
    values: runState.values,
    refreshToken: refreshToken,
    requestConfig: viewConfig,
    updateDevTools: handlers.updateDevTools,
    setEtag: handlers.setEtag,
    runReport: biApi.uiReports.runChartReport,
  });

  return (
    <>
      <ChartReportExport
        reportName={reportName}
        warning={getChartExportWarning(state)}
        onExport={(fileName, format) => exportChartRef.current?.(fileName, format)}
      />
      <ViewContainer
        Parameters={
          runState.showParameters ? (
            <ViewerParameters
              fieldsWithConditions={runState.fieldsWithCustomConditionParameters}
              initialDetailedView={runState.settings.detailedView}
            />
          ) : (
            <></>
          )
        }
        Preview={
          <PreviewComponent
            state={state}
            exportFuncCallback={(callback) => {
              exportChartRef.current = callback;
            }}
            argumentFields={runState.arguments}
            values={runState.values}
            reportType={reportType}
            settings={runState.settings}
            onDrillDown={handlers.onDrillDown}
            previewMode={previewMode}
          />
        }
        ActionPanel={<PreviewActions onRefresh={onRefreshGrid} ChangePreviewModeButton={<ChangePreviewModeButton />} />}
      />
    </>
  );
}
